import { Box, Center, Group, RingProgress, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { PROFILE_URL } from '../../lib/profileNav';
import type { ProductStats, UserStats } from './orgStats';

type LegendItem = {
  label: string;
  value: string | number;
  color: string;
};

type StatData = {
  label: string;
  segments: Array<{ value: number; color: string }>;
  legend: LegendItem[];
  ringlabel?: React.ReactNode;
  to?: string;
  children?: React.ReactNode;
};

const valueToPercent = (value: number, total: number) => (total > 0 ? (value / total) * 100 : 0);

function StatCard({ label, segments, legend, ringlabel, to, children }: Readonly<StatData>) {
  return (
    <Box h={'auto'} p={'18px 20px'} data-product-tile={true} component={to ? NavLink : undefined} to={`${PROFILE_URL}/${to ?? ''}`}
      ta={'left'}
      style={{
        '--transform': '-2px',
        '--shadow-hover': 'var(--shadow-md)',
        '--border-hover': 'var(--brand-soft-bd)',
        background: 'var(--bg-surface)',
      }}
    >
      <Stack gap={12} w={'100%'}>
        <Text fz="xs" fw={700} tt="uppercase" c="var(--text-subtle)" lts={0.4}>
          {label}
        </Text>
        <Group align="center" justify="space-between" wrap="nowrap" gap={18}>
          <Box style={{ position: 'relative', flex: 'none' }} size={104}>
            <RingProgress
              size={104}
              thickness={6}
              sectionGap={4}
              sections={segments}
              label={ringlabel}
            />

          </Box>
          <Stack gap={5} style={{ minWidth: 0, flex: 1 }}>
            {legend.map((item) => (
              <Group key={item.label} justify="space-between" gap={10} wrap="nowrap">
                <Group gap={6} wrap="nowrap">
                  <Box
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: item.color,
                      flex: 'none',
                    }}
                  />
                  <Text fz="xs" c="var(--text-muted)" lh={'normal'}>
                    {item.label}
                  </Text>
                </Group>
                <Text fz="xs" fw={700} c={item.color} ta="right" lh={'normal'}>
                  {item.value}
                </Text>
              </Group>
            ))}
            {children}
          </Stack>
        </Group>
      </Stack>
    </Box>
  );
}

export function TotalUsersStat({ stats }: Readonly<{ stats: UserStats }>) {
  const { active, invited, suspended, total } = stats;
  const showSuspended = suspended > 0;

  const segments = useMemo(() => {
    const sections = [
      { value: valueToPercent(active, total), color: '#22c55e' },
      { value: valueToPercent(invited, total), color: '#f59e0b' },
    ];
    if (showSuspended) sections.push({ value: valueToPercent(suspended, total), color: 'var(--status-danger)' });
    return sections;
  }, [active, invited, suspended, total, showSuspended]);

  const legend = useMemo(() => {
    const items = [
      { label: 'Active', value: active, color: '#22c55e' },
      { label: 'Invited', value: invited, color: '#f59e0b' },
    ];
    if (showSuspended) items.push({ label: 'Suspended', value: suspended, color: 'var(--status-danger)' });
    return items;
  }, [active, invited, suspended, showSuspended]);

  const ringlabel = useMemo(() => (
    <Center style={{ flexDirection: 'column' }}>
      <Text fz={22} fw={700} c="var(--text-default)" lh={'normal'}>
        {total}
      </Text>
      <Text fz={10} lh={'normal'} fw={600} c={'var(--text-muted)'}>users</Text>
    </Center>
  ), [total]);

  return <StatCard to='users' label='Total users' segments={segments} legend={legend} ringlabel={ringlabel} />;
}

export function ActiveProductsStat({ stats }: Readonly<{ stats: ProductStats }>) {
  const { active, inactive, total } = stats;

  const segments = useMemo(() => [
    { value: valueToPercent(active, total), color: '#22c55e' },
    { value: valueToPercent(inactive, total), color: '#94a3b8' },
  ], [active, inactive, total]);

  const legend = useMemo(() => [
    { label: 'Active', value: active, color: '#22c55e' },
    { label: 'Inactive', value: inactive, color: '#94a3b8' },
  ], [active, inactive]);

  const ringlabel = useMemo(() => (
    <Center style={{ flexDirection: 'column' }}>
      <Text fz={22} fw={700} c="var(--text-default)" lh={'normal'}>
        {`${active}/${total}`}
      </Text>
      <Text fz={10} lh={'normal'} fw={600} c={'var(--text-muted)'}>active</Text>
    </Center>
  ), [active, total]);

  return <StatCard to='entitlements' label='Active products' segments={segments} legend={legend} ringlabel={ringlabel} />;
}

export function PendingInvitesStat({ stats }: Readonly<{ stats: UserStats }>) {
  const { active, invited } = stats;
  const shownTotal = active + invited;

  const segments = useMemo(() => [
    { value: valueToPercent(active, shownTotal), color: '#22c55e' },
    { value: valueToPercent(invited, shownTotal), color: '#f59e0b' },
  ], [active, invited, shownTotal]);

  const legend = useMemo(() => [
    { label: 'Accepted', value: active, color: '#22c55e' },
    { label: 'Pending', value: invited, color: '#f59e0b' },
  ], [active, invited]);

  const ringlabel = useMemo(() => (
    <Center style={{ flexDirection: 'column' }}>
      <Text fz={22} fw={700} c="var(--text-default)" lh={'normal'}>
        {invited}
      </Text>
      <Text fz={10} lh={'normal'} fw={600} c={'var(--text-muted)'}>pending</Text>
    </Center>
  ), [invited]);

  return <StatCard to='users?status=Invited' label='Pending invites' segments={segments} legend={legend} ringlabel={ringlabel} />;
}
